import { EditablePersonInfo } from '@/data/Content/PersonInfo';
import { useForm } from '@/utils/useForm';
import { formatPhoneNumber, formatPhoneNumberValue } from '@/utils/InputPhone';
import { formatName } from '@/utils/InputName';
import { formatDateOfBirth } from '@/utils/inputBirthDate';
import {
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from '@mui/material';
import { ChangeEvent, FC, FocusEvent, useEffect, useMemo, useState } from 'react';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REG_EX } from '@/utils/address';
import { AccountPersonalButtonSX } from '@/components/content/Account/styles/accountPersonal';
import { CheckboxProps } from '@mui/material/Checkbox';
import { DpCheckbox } from '@/components/blocks/DpCheckbox';
import {
	AccountInformationFormCheckboxSX,
	AccountBlockedEmailSX,
	AccountInformationFormTitleSX,
	AccountInformationBpCheckboxSX,
	FormControlLabelSX,
} from '@/components/content/Account/styles/accountInformationForm';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { AccountSelectSX } from '@/components/content/Account/styles/accountPersonal';
import { AccountDropdownIcon } from '@/components/content/Account/parts/icons/AccountDropdownIcon';
import { usePreferredSizes } from 'integration/hooks/usePreferredSizes';
import { useInterest } from 'integration/hooks/useInterest';
import { AccountPreferredSizesCard } from '@/components/content/Account/parts/AccountPreferredSizesCard';
import { AccountInterestsCard } from '@/components/content/Account/parts/AccountInterestsCard';
import { AccountHobbiesCard } from '@/components/content/Account/parts/AccountHobbiesCard';
import {
	convertDateToBackendFormat,
	formatDateToDisplay,
} from 'integration/utils/convertDateToBackendFormat';
import { useHobbies } from '@/data/Content/Hobbies';
interface AccountInformationFormProps {
	personInfo: EditablePersonInfo;
	onSubmit: (personInfo: EditablePersonInfo) => void;
	isLoading?: boolean;
}
type EditablePersonInfoKeys = keyof EditablePersonInfo;

const initialValuesError = {
	addressLine1: false,
	addressLine2: false,
	city: false,
	country: false,
	dateOfBirth: false,
	email1: false,
	firstName: false,
	gender: false,
	lastName: false,
	lastNameMat: false,
	parentOrgId: false,
	phone1: false,
	receiveEmail: false,
	state: false,
	zipCode: false,
};

export const BpCheckbox = (props: CheckboxProps) => (
	<DpCheckbox
		sx={{
			'&:hover': { bgcolor: 'transparent' },
		}}
		disableRipple
		color="default"
		inputProps={{ 'aria-label': 'Checkbox demo' }}
		{...props}
	/>
);

export const AccountInformationForm: FC<AccountInformationFormProps> = ({
	personInfo,
	onSubmit,
	isLoading,
}) => {
	const {
		values,
		handleSubmit,
		formRef,
		handleInputChange,
		handleSelectChange,
		onNamedValueChange,
	} = useForm(personInfo);

	const [error, setError] = useState(initialValuesError);

	const CustomIconComponent = (props: any) => <AccountDropdownIcon {...props} />;
	const [isDateModified, setIsDateModified] = useState(false);

	const formattedDateOfBirth = useMemo(
		() => formatDateToDisplay(values.dateOfBirth ?? ''),
		[values.dateOfBirth]
	);

	const handleInputChangeWithDate = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.name === 'dateOfBirth') {
			setIsDateModified(true);
		}
		handleInputChange(event);
		setError((prev) => ({
			...prev,
			[event.target.name]: false,
		}));
	};

	const validateFields = (): boolean => {
		let isValid = true;
		const newError = { ...error };

		const cleanedPhoneNumber = values.phone1.replace(/\D/g, '');
		if (!cleanedPhoneNumber) {
			newError.phone1 = true;
			isValid = false;
		} else if (cleanedPhoneNumber.length !== 10) {
			newError.phone1 = true;
			isValid = false;
		}

		if (isDateModified || !values.dateOfBirth) {
			if (values.dateOfBirth) {
				const cleanedDateOfBirth = values.dateOfBirth.replace(/[^0-9]/g, '');
				if (!cleanedDateOfBirth) {
					newError.dateOfBirth = true;
					isValid = false;
				} else if (cleanedDateOfBirth.length !== 8) {
					newError.dateOfBirth = true;
					isValid = false;
				} else if (!REG_EX.BIRTH_DATE.test(values.dateOfBirth)) {
					newError.dateOfBirth = true;
					isValid = false;
				}
			} else {
				newError.dateOfBirth = true;
				isValid = false;
			}
		}
		if (setError) {
			setError(newError);
		}

		return isValid;
	};

	const handleFormSubmit = () => {
		const isValid = validateFields();
		if (isValid) {
			const trimPersonInfo = {
				...values,
				firstName: values.firstName.trim(),
				lastName: values.lastName.trim(),
				lastNameMat: values.lastNameMat?.trim(),
			};

			const updatedPersonInfo = {
				...trimPersonInfo,
				dateOfBirth: formattedDateOfBirth ? convertDateToBackendFormat(formattedDateOfBirth) : '',
			};

			onSubmit(updatedPersonInfo);
			setIsDateModified(false);
		}
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		const { name } = e.target;
		const key = name as EditablePersonInfoKeys;
		if (!values[key]) {
			setError((prev) => ({
				...prev,
				[name]: true,
			}));
		}
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		handleInputChange(e);
		setError((prev) => ({
			...prev,
			[name]: false,
		}));
	};
	const helperText = useMemo(
		() => ({
			firstName: error.firstName ? 'Captura tu Nombre' : EMPTY_STRING,
			lastName: error.lastName ? 'Captura tu Apellido paterno' : EMPTY_STRING,
			lastNameMat: error.lastNameMat ? 'Captura tu Apellido materno' : EMPTY_STRING,
			phone1: error.phone1
				? values.phone1.length === 0
					? 'Captura tu Teléfono'
					: 'Teléfono inválido'
				: EMPTY_STRING,
			email1: error.email1 ? 'Correo inválido' : EMPTY_STRING,
			dateOfBirth: error.dateOfBirth
				? !values.dateOfBirth || values.dateOfBirth.length === 0
					? 'Captura tu Fecha de nacimiento'
					: 'Fecha inválida'
				: EMPTY_STRING,
		}),
		[error, values.phone1, values.dateOfBirth]
	);

	const { onInitPreferredSize, ...restUsePreferredSizes } = usePreferredSizes();

	useEffect(() => {
		onInitPreferredSize(personInfo.x_userProfileField1);
	}, [personInfo.x_userProfileField1]);

	useEffect(() => {
		onNamedValueChange('x_userProfileField1', JSON.stringify(restUsePreferredSizes.selectedSizes));
	}, [restUsePreferredSizes.selectedSizes]);

	const restUseInterest = useInterest();
	const restUseHobbies = useHobbies();

	return (
		<Stack component="form" noValidate ref={formRef} onSubmit={handleSubmit(handleFormSubmit)}>
			<Typography component={'h1'} sx={AccountInformationFormTitleSX}>
				{'INFORMACIÓN PERSONAL'}
			</Typography>

			<Grid container spacing={2} marginBottom={2}>
				<Grid item xs={12}>
					<DpOutlinedInput
						required
						id="address-form-email"
						data-testid="address-form-email"
						name="email1"
						type="email"
						label="Correo electrónico"
						value={values.email1}
						error={error.email1}
						onBlur={handleBlur}
						helperText={helperText.email1}
						inputProps={{
							maxLength: 35,
							pattern: REG_EX.EMAIL.source,
							placeholder: 'name@domain.com',
							readOnly: true,
							tabIndex: -1,
						}}
						fullWidth
						autoComplete="email"
						onChange={onChange}
						sx={AccountBlockedEmailSX}
						disabled={true}
					/>

					<FormControlLabel
						sx={FormControlLabelSX}
						control={
							<BpCheckbox
								data-testid={`schedule-recurring-checkbox`}
								id={`schedule-recurring-checkbox`}
								name={`receiveEmail`}
								checked={values.receiveEmail}
								onChange={handleInputChange}
								sx={AccountInformationBpCheckboxSX}
							/>
						}
						label={
							<Typography sx={AccountInformationFormCheckboxSX}>
								{
									'Deseo recibir promociones y noticias en mi correo electrónico por parte de dportenis.'
								}
							</Typography>
						}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} marginBottom={2}>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						id="address-form-firstName"
						data-testid="address-form-firstName"
						name="firstName"
						label="Nombre"
						value={values.firstName}
						inputProps={{ maxLength: 50 }}
						fullWidth
						error={error.firstName}
						autoComplete="given-name"
						required
						helperText={helperText.firstName}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="address-form-lastName"
						data-testid="address-form-lastName"
						name="lastName"
						error={error.lastName}
						label="Apellido paterno"
						value={values.lastName}
						inputProps={{ maxLength: 50 }}
						fullWidth
						autoComplete="family-name"
						helperText={helperText.lastName}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} marginBottom={2}>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="address-form-lastNameMat"
						data-testid="address-form-lastNameMat"
						name="lastNameMat"
						error={error.lastNameMat}
						label="Apellido materno"
						value={values.lastNameMat}
						inputProps={{ maxLength: 50 }}
						fullWidth
						autoComplete="family-name"
						helperText={helperText.lastNameMat}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="address-form-phone1"
						name="phone1"
						type="tel"
						data-testid="address-form-phone1"
						label="Teléfono"
						value={formatPhoneNumberValue(values.phone1)}
						error={error.phone1}
						helperText={helperText.phone1}
						inputProps={{ maxLength: 12, pattern: REG_EX.PHONE.source }}
						fullWidth
						autoComplete="tel"
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatPhoneNumber(event, onChange)}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} marginBottom={2}>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="address-form-dateOfBirth"
						name="dateOfBirth"
						data-testid="address-form-dateOfBirth"
						label="Fecha de nacimiento"
						value={formattedDateOfBirth}
						error={error.dateOfBirth}
						helperText={helperText.dateOfBirth}
						inputProps={{
							maxLength: 10,
							pattern: REG_EX.BIRTH_DATE.source,
							placeholder: 'dd/mm/aaaa',
						}}
						fullWidth
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							formatDateOfBirth(event, handleInputChangeWithDate)
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormControl variant="outlined" fullWidth>
						<InputLabel id="demo-simple-select-helper-label">{'Género'}</InputLabel>
						<Select
							labelId="demo-simple-select-helper-label"
							id="demo-simple-select-helper"
							name="gender"
							value={values.gender || ''}
							error={error.gender}
							fullWidth
							onChange={handleSelectChange}
							label="Género"
							IconComponent={CustomIconComponent}
							sx={AccountSelectSX}
						>
							<MenuItem value="Male">{'Masculino'}</MenuItem>
							<MenuItem value="Female">{'Femenino'}</MenuItem>
							<MenuItem value="Unspecified">{'Prefiero no decirlo'}</MenuItem>
						</Select>
					</FormControl>
				</Grid>
			</Grid>

			<Divider
				sx={{
					marginBottom: '20px',
					marginTop: '10px',
				}}
			/>

			<AccountPreferredSizesCard {...restUsePreferredSizes} gender={values.gender} />

			<AccountInterestsCard {...restUseInterest} />

			<AccountHobbiesCard {...restUseHobbies} />

			<Button
				variant="contained"
				size="small"
				type="submit"
				data-testid="button-personal-information-save-edit"
				id="button-personal-information-save-edit"
				fullWidth
				sx={AccountPersonalButtonSX}
				disabled={isLoading}
			>
				{'Actualizar datos'}
			</Button>
		</Stack>
	);
};
